import React, { useEffect, useState } from "react";
import { client, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const LABEL = "font-condensed uppercase tracking-wider text-[11px] font-semibold mb-1";
const INPUT = "h-9 rounded-[10px] px-2 text-[13px] bg-white";

function ClosedSummary({ job }) {
  return (
    <div className="space-y-4" data-testid="review-readonly">
      <div>
        <div className={LABEL} style={{ color: "#5B6472" }}>Labour hours</div>
        <div className="text-[13px]" data-testid="review-readonly-labour">{job.labour_hours ?? 0} h</div>
      </div>
      <div>
        <div className={LABEL} style={{ color: "#5B6472" }}>Parts</div>
        {(job.parts_used || []).length === 0 && <div className="text-[12.5px]" style={{ color: "#8A93A1" }}>No parts recorded</div>}
        {(job.parts_used || []).map((part, index) => (
          <div key={`${part.name}-${index}`} className="text-[13px] py-0.5" data-testid={`review-readonly-part-${index}`}>
            {part.name} × {part.qty || 1}{part.unit_price ? ` · ${part.unit_price}` : ""}
            {part.covered ? " · Covered" : ""}{part.billable ? " · Billable" : ""}
          </div>
        ))}
      </div>
      <div>
        <div className={LABEL} style={{ color: "#5B6472" }}>Photos</div>
        {(job.photos || []).length === 0 && <div className="text-[12.5px]" style={{ color: "#8A93A1" }}>No photos</div>}
        <div className="flex flex-wrap gap-2">
          {(job.photos || []).filter(p => p?.file_id).map(photo => (
            <img key={photo.file_id} src={`${API}/files/${photo.file_id}`} alt={photo.caption || ""}
                 title={photo.caption || ""} className="w-14 h-14 rounded-[8px] object-cover" />
          ))}
        </div>
      </div>
      <div className="text-[12.5px]" style={{ color: "#5B6472" }} data-testid="review-closed-by">
        Closed {job.closed_at ? new Date(job.closed_at).toLocaleString("en-GB", { timeZone: "America/Barbados" }) : ""}
        {job.closed_by ? ` by ${job.closed_by}` : ""}
      </div>
    </div>
  );
}

const blankPart = () => ({ name: "", qty: 1, unit_price: "", covered: false, billable: false });

function PartsEditor({ parts, setParts }) {
  const change = (index, patch) => setParts(list => list.map((p, i) => i === index ? { ...p, ...patch } : p));
  return (
    <div>
      <div className={LABEL} style={{ color: "#5B6472" }}>Parts</div>
      {parts.length === 0 && <div className="text-[12.5px]" style={{ color: "#8A93A1" }}>No parts recorded</div>}
      {parts.map((part, index) => (
        <div key={index} className="flex items-center gap-2 flex-wrap py-1.5" data-testid={`review-part-${index}`}>
          <input placeholder="Part name" className={`${INPUT} flex-1 min-w-[140px]`} style={{ border: "1px solid #E7EAEE" }}
            value={part.name || ""} onChange={event => change(index, { name: event.target.value })}
            data-testid={`review-part-name-${index}`} />
          <input type="number" step="1" min="1" placeholder="Qty" className={`${INPUT} w-[64px]`}
            style={{ border: "1px solid #E7EAEE" }} value={part.qty ?? ""}
            onChange={event => change(index, { qty: event.target.value })}
            data-testid={`review-part-qty-${index}`} />
          <input type="number" step="0.01" min="0" placeholder="Price"
            className={`${INPUT} w-[92px]`} style={{ border: "1px solid #E7EAEE" }}
            value={part.unit_price ?? ""}
            onChange={event => change(index, { unit_price: event.target.value })}
            data-testid={`review-part-price-${index}`} />
          <label className="text-[12px] flex items-center gap-1">
            <input type="checkbox" checked={!!part.covered}
              onChange={event => change(index, { covered: event.target.checked })}
              data-testid={`review-part-covered-${index}`} />
            Covered
          </label>
          <label className="text-[12px] flex items-center gap-1">
            <input type="checkbox" checked={!!part.billable}
              onChange={event => change(index, { billable: event.target.checked })}
              data-testid={`review-part-billable-${index}`} />
            Billable
          </label>
          <Button variant="outline" className="h-9 bf-btn-ghost text-[12px]"
            onClick={() => setParts(list => list.filter((_, i) => i !== index))}
            data-testid={`review-part-remove-${index}`}>
            Remove
          </Button>
        </div>
      ))}
      <Button variant="outline" className="h-8 mt-1 bf-btn-ghost text-[12px]"
        onClick={() => setParts(list => [...list, blankPart()])} data-testid="review-part-add">
        Add part
      </Button>
    </div>
  );
}

function WriteupEditor({ work, setWork, leaving, setLeaving }) {
  return (
    <>
      <div>
        <div className={LABEL} style={{ color: "#5B6472" }}>Work performed / findings</div>
        <textarea rows={4} maxLength={5000} className="w-full rounded-[10px] px-2 py-1.5 text-[13px] bg-white"
          style={{ border: "1px solid #E7EAEE" }} value={work} onChange={event => setWork(event.target.value)}
          data-testid="review-work-input" />
      </div>
      <div>
        <div className={LABEL} style={{ color: "#5B6472" }}>Equipment status on leaving</div>
        <select className={`${INPUT} w-[220px]`} style={{ border: "1px solid #E7EAEE" }} value={leaving}
          onChange={event => setLeaving(event.target.value)} data-testid="review-leaving-select">
          {!leaving && <option value="">Not recorded</option>}
          <option value="running">Running</option>
          <option value="running-monitor">Running — monitor</option>
          <option value="out_of_service">Out of service</option>
        </select>
      </div>
    </>
  );
}

function PhotosEditor({ photos, setPhotos }) {
  return (
    <div>
      <div className={LABEL} style={{ color: "#5B6472" }}>Photos</div>
      {photos.length === 0 && <div className="text-[12.5px]" style={{ color: "#8A93A1" }}>No photos</div>}
      <div className="space-y-2">
        {photos.map((photo, index) => (
          <div key={photo.file_id || index} className="flex items-center gap-2" data-testid={`review-photo-${index}`}>
            {photo.file_id && (
              <img src={`${API}/files/${photo.file_id}`} alt="" className="w-14 h-14 rounded-[8px] object-cover" />
            )}
            <input placeholder="Caption" className={`${INPUT} flex-1`} style={{ border: "1px solid #E7EAEE" }}
              value={photo.caption || ""}
              onChange={event => setPhotos(list => list.map((p, i) => i === index ? { ...p, caption: event.target.value } : p))}
              data-testid={`review-photo-caption-${index}`} />
            <Button variant="outline" className="h-9 bf-btn-ghost text-[12px]"
              onClick={() => setPhotos(list => list.filter((_, i) => i !== index))}
              data-testid={`review-photo-remove-${index}`}>
              Remove
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function JobReviewDialog({ job, onClose, onDone }) {
  const [photos, setPhotos] = useState([]);
  const [labour, setLabour] = useState("");
  const [parts, setParts] = useState([]);
  const [work, setWork] = useState("");
  const [leaving, setLeaving] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!job) return;
    setPhotos((job.photos || []).map(p => (typeof p === "string" ? { url: p } : { ...p })));
    setLabour(String(job.labour_hours ?? ""));
    setParts((job.parts_used || []).map(p => ({ ...p })));
    setWork(job.work_performed || "");
    setLeaving(job.equipment_status_on_leaving || "");
  }, [job]);

  if (!job) return null;
  const closed = job.status === "complete";

  const payload = () => ({
    photos: photos.filter(p => p.file_id).map(p => ({ file_id: p.file_id, caption: p.caption || "" })),
    labour_hours: Number(labour) || 0,
    work_performed: work,
    ...(leaving ? { equipment_status_on_leaving: leaving } : {}),
    parts_used: parts.map(p => ({
      ...p, name: (p.name || "").trim(), qty: Number(p.qty) || 0,
      unit_price: Number(p.unit_price) || 0, covered: !!p.covered, billable: !!p.billable,
    })),
  });

  const submit = async (status, message) => {
    try {
      setBusy(true);
      await client.patch(`/jobs/${job.id}`, { ...payload(), status });
      toast.success(message);
      onDone();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update this job");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-[560px] max-h-[88vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle>{closed ? `${job.ticket_number} (closed)` : `Review ${job.ticket_number}`}</DialogTitle>
          <DialogDescription>
            {closed ? "This job is closed and its report is frozen." : "Check and correct the technician's close-out before the report is frozen. The signature cannot be changed."}
          </DialogDescription>
        </DialogHeader>

        {closed ? <ClosedSummary job={job} /> : (
          <div className="space-y-4">
            <div>
              <div className={LABEL} style={{ color: "#5B6472" }}>Labour hours</div>
              <input type="number" step="0.25" min="0" className={`${INPUT} w-[140px]`}
                style={{ border: "1px solid #E7EAEE" }}
                value={labour} onChange={event => setLabour(event.target.value)}
                data-testid="review-labour-input" />
            </div>
            <WriteupEditor work={work} setWork={setWork} leaving={leaving} setLeaving={setLeaving} />
            <PartsEditor parts={parts} setParts={setParts} />
            <PhotosEditor photos={photos} setPhotos={setPhotos} />
          </div>
        )}

        <DialogFooter className="mt-3">
          {closed ? (
            <>
              <Button variant="outline" className="bf-btn-ghost" onClick={onClose} data-testid="review-close-btn">Close</Button>
              <a href={`${API}/jobs/${job.id}/report.pdf`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center justify-center h-9 px-4 rounded-md text-[13px] bf-btn-primary"
                 data-testid="review-report-pdf-link">
                Report PDF
              </a>
            </>
          ) : (
            <>
              <Button variant="outline" className="bf-btn-ghost" disabled={busy}
                onClick={() => submit("on_site", `${job.ticket_number} returned to the technician`)}
                data-testid="return-to-tech-btn">
                Return to tech
              </Button>
              <Button className="bf-btn-primary px-4" disabled={busy}
                onClick={() => submit("complete", `${job.ticket_number} closed`)}
                data-testid="final-close-btn">
                Final close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
